import { identity } from 'ramda'
import React, { useState, Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { formatOptionsFromCollection } from '../../utils/helpers/select'
import Menu, { MenuItem } from '../navigation/Menu'
import Plain from '../helpers/Plain'
import PopoverInput from './PopoverInput'
import Scroll from '../helpers/Scroll'
import { COLORS } from '@btime/constants'

const LazySelectContent = ({
  dataHook,
  handleChange,
  mapOptions,
  labelKey,
  value,
  hasValue,
  handleCreate,
  canCreate,
  renderLabel,
  search,
  filter,
  t
}) => {
  const data = dataHook()
  let options = !!mapOptions
    ? mapOptions(data || [])
    : formatOptionsFromCollection(labelKey || 'name', 'id', data || [])
  options = options.filter(filter || identity)
  const noOptions = !options || !options.length

  if ((!canCreate || !search) && noOptions) {
    const base = t('lazySelect.noItems', 'Nenhum item encontrado')
    const withCreate =
      !!canCreate && search
        ? t('lazySelect.noItemsWithCreate', 'Nenhum item encontrado, digite para cadastrar um novo')
        : base

    return <MenuItem iconLeft="BsFillInboxFill">{withCreate}</MenuItem>
  }

  return (
    <Fragment>
      {!search && !!hasValue && (
        <MenuItem
          color={COLORS.dark4}
          iconLeft="BsX"
          onClick={handleChange({ label: '', value: undefined, origin: {} })}
        >
          {t('removeSelection')}
        </MenuItem>
      )}

      {options.map(option => (
        <MenuItem onClick={handleChange(option)} key={option.value + option.label}>
          {t(renderLabel ? renderLabel(option, { search }) : option.label)}
        </MenuItem>
      ))}

      {!!canCreate && !!search && (
        <MenuItem color={COLORS.purple} iconLeft="BsPlusCircle" onClick={handleCreate(search)}>
          {t('lazySelect.createPrefix', 'Cadastrar:')} {search}
        </MenuItem>
      )}
    </Fragment>
  )
}

const LazySelect = ({
  t,
  Loader,
  loading,
  loaderProps,
  dataHook,
  onChange,
  selectedLabel,
  value,
  onCreate,
  labelKey,
  mapOptions,
  renderLabel,
  filter,
  ...props
}) => {
  const [search, setSearch] = useState('')
  Loader = Loader || Plain
  const { t: tComponents } = useTranslation(['common', 'components'])
  t = t || tComponents || identity

  const handleChange = React.useCallback(
    onClose => option => () => {
      onChange(option.value, option.label, option)
      onClose()
    },
    [onChange]
  )

  const handleCreate = React.useCallback(
    onClose => name => () => {
      onCreate(name)
      onClose()
    },
    [onCreate]
  )

  return (
    <PopoverInput
      value={t(selectedLabel || value || '')}
      onChangeSearch={setSearch}
      search={search}
      useSearch
      loading={loading}
      {...props}
      render={({ onClose }) => (
        <Menu>
          <Scroll>
            <Loader search={search} disableEmpty perPage={10} {...(loaderProps || {})}>
              <LazySelectContent
                t={t}
                search={search}
                dataHook={dataHook}
                handleChange={handleChange(onClose)}
                value={value}
                hasValue={!!value || !!selectedLabel}
                canCreate={!!onCreate}
                handleCreate={handleCreate(onClose)}
                labelKey={labelKey}
                mapOptions={mapOptions}
                renderLabel={renderLabel}
                filter={filter}
              />
            </Loader>
          </Scroll>
        </Menu>
      )}
    />
  )
}

export default LazySelect
